import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { getLogger } from 'app-components/utils/core';

import { ROOT_MODAL_DRAWER_PROP } from 'components/Drawers/standardDrawerContract';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

import { getModalProps } from './drawerPresentation';
import { MODAL_COMPONENTS } from './modalRegistry';
import type { AsyncModalModule, ModalComponent, ModalProps, RegistryComponent, RootModalRecord } from './types';

const log = getLogger('components/RootModal/ModalRenderer');

type AsyncModalWrapperProps = {
  component: Promise<AsyncModalModule>;
  modal: RootModalRecord;
  modalProps: ModalProps;
};

type ModalRendererProps = {
  dialogType: string;
  modal: RootModalRecord;
  onRootModalDrawerClose?: () => void;
  onRootModalDrawerDirtyChange?: (isDirty: boolean) => void;
  rootModalDrawer?: RootModalDrawerOptions;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const resolveModalComponent = (resolvedComponent: AsyncModalModule): ModalComponent => {
  if (isObject(resolvedComponent) && resolvedComponent.default) {
    return resolvedComponent.default as ModalComponent;
  }

  return resolvedComponent as ModalComponent;
};

function AsyncModalLoadingFallback({ rootModalDrawer }: { rootModalDrawer?: RootModalDrawerOptions }) {
  if (!rootModalDrawer) {
    return null;
  }

  return (
    <Box
      aria-label='Loading dialog'
      data-testid='root-modal-drawer-loading'
      role='status'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        p: 3,
      }}
    >
      <Skeleton variant='text' width='55%' height={40} />
      <Skeleton variant='rounded' width='100%' height={72} />
      <Skeleton variant='rounded' width='100%' height={120} />
      <Skeleton variant='rounded' width='100%' height={180} />
    </Box>
  );
}

const AsyncModalWrapper = React.memo(function AsyncModalWrapper({
  component,
  modal,
  modalProps,
}: AsyncModalWrapperProps) {
  const [ModalComponent, setModalComponent] = useState<ModalComponent | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    setLoadFailed(false);
    component
      .then((resolvedComponent) => {
        if (isMountedRef.current) {
          const ResolvedModalComponent = resolveModalComponent(resolvedComponent);
          setModalComponent(() => ResolvedModalComponent);
        }
      })
      .catch((error) => {
        log.error('Failed to load modal component:', error);
        if (isMountedRef.current) {
          setLoadFailed(true);
        }
      });

    return () => {
      isMountedRef.current = false;
    };
  }, [component]);

  if (loadFailed) {
    return (
      <Box aria-label='Unable to load dialog' role='alert' sx={{ p: 3 }}>
        <Typography variant='titleMedium'>Unable to load this drawer</Typography>
        <Typography color='text.secondary' sx={{ mt: 1 }} variant='bodyRegular'>
          Close it and try again.
        </Typography>
      </Box>
    );
  }

  if (!ModalComponent) {
    return <AsyncModalLoadingFallback rootModalDrawer={modalProps[ROOT_MODAL_DRAWER_PROP] as RootModalDrawerOptions} />;
  }

  return <ModalComponent type={modal.type} dialogId={modal.id} {...modalProps} />;
});

function ModalRenderer({
  modal,
  dialogType,
  onRootModalDrawerClose,
  onRootModalDrawerDirtyChange,
  rootModalDrawer,
}: ModalRendererProps) {
  const component = MODAL_COMPONENTS[dialogType] as RegistryComponent | undefined;

  if (!modal || !component) {
    return null;
  }

  const modalProps = getModalProps(modal);
  if (rootModalDrawer) {
    modalProps.onRootModalDrawerClose = onRootModalDrawerClose;
    modalProps.onRootModalDrawerDirtyChange = onRootModalDrawerDirtyChange;
    modalProps[ROOT_MODAL_DRAWER_PROP] = rootModalDrawer;
  }

  // ImportModalComponent exposes async getters so development hot reload sees
  // the latest dialog implementation without re-registering every modal.
  if (component instanceof Promise) {
    return <AsyncModalWrapper component={component} modal={modal} modalProps={modalProps} />;
  }

  const ModalComponent = component;
  return <ModalComponent type={modal.type} dialogId={modal.id} {...modalProps} />;
}

export default React.memo(ModalRenderer);
